(function () {
  'use strict';

  /* Feature spotlight interactivity:
     - [data-cg-carousel]  scroll-snap carousel (D images, F cards) with
       prev/next nav, generated dots, and disabled-state at the ends.
     - [data-cg-video]     click-to-play overlay for the Section E video.
     Scoped entirely to .cg-spotlight; no globals. */

  // Carousels scroll instantly when the visitor prefers reduced motion.
  function scrollBehavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
  }

  function initCarousel(root) {
    if (root.dataset.cgCarouselInit === '1') return;
    root.dataset.cgCarouselInit = '1';

    const viewport = root.querySelector('[data-cg-carousel-viewport]');
    const track = viewport && viewport.querySelector('.cg-spotlight__carousel-track');
    if (!viewport || !track) return;

    const slides = Array.prototype.slice.call(track.children);
    if (slides.length === 0) return;

    const prevBtn = root.querySelector('[data-cg-carousel-prev]');
    const nextBtn = root.querySelector('[data-cg-carousel-next]');
    const dotsWrap = root.querySelector('[data-cg-carousel-dots]');

    // Optional copy panels kept in sync with the active slide (Section D).
    // Empty for carousels without panels (Section F) — sync is a no-op.
    const section = root.closest('section');
    const panels = section
      ? Array.prototype.slice.call(section.querySelectorAll('[data-cg-carousel-panel]'))
      : [];

    function step() {
      const styles = getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return slides[0].getBoundingClientRect().width + gap;
    }

    // One dot per scrollable page, not per slide. When several slides
    // share the viewport the page count is lower than slides.length, and
    // it shifts with the breakpoint — so dots are (re)built to match.
    const dots = [];

    function pageCount() {
      const s = step();
      if (s <= 0) return 1;
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      if (maxScroll <= 2) return 1;
      return Math.ceil((maxScroll - 2) / s) + 1;
    }

    function buildDots() {
      if (!dotsWrap) return;
      const count = pageCount();
      if (dots.length === count) return;
      dotsWrap.innerHTML = '';
      dots.length = 0;
      for (let i = 0; i < count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cg-spotlight__carousel-dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener(
          'click',
          (function (page) {
            return function () {
              viewport.scrollTo({ left: step() * page, behavior: scrollBehavior() });
            };
          })(i)
        );
        dotsWrap.appendChild(dot);
        dots.push(dot);
      }
    }

    function update() {
      const s = step();
      const maxScroll = viewport.scrollWidth - viewport.clientWidth - 2;
      const atEnd = viewport.scrollLeft >= maxScroll;
      // Snap the active index to the last page once scrolling can go no
      // further. When several slides share the viewport the final scroll
      // position rounds short of the last index, which makes the
      // pagination look like one slide is still pending.
      const last = (dots.length || slides.length) - 1;
      let idx = s > 0 ? Math.round(viewport.scrollLeft / s) : 0;
      if (atEnd) idx = last;
      if (idx < 0) idx = 0;
      if (idx > last) idx = last;
      dots.forEach(function (dot, i) {
        dot.classList.toggle('cg-spotlight__carousel-dot--active', i === idx);
      });
      panels.forEach(function (panel, i) {
        panel.classList.toggle('cg-spotlight__unwind-panel--active', i === idx);
      });
      if (prevBtn) prevBtn.disabled = viewport.scrollLeft <= 2;
      if (nextBtn) nextBtn.disabled = atEnd;
    }

    function refresh() {
      buildDots();
      update();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        viewport.scrollBy({ left: -step(), behavior: scrollBehavior() });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        viewport.scrollBy({ left: step(), behavior: scrollBehavior() });
      });
    }

    let raf;
    viewport.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    });
    // Observe the viewport itself rather than binding a window 'resize'
    // listener — the observer is scoped to this element and is GC'd with
    // it when the Theme Editor re-renders the section, so nothing leaks.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(refresh).observe(viewport);
    } else {
      window.addEventListener('resize', refresh);
    }
    refresh();
  }

  function initVideo(root) {
    if (root.dataset.cgVideoInit === '1') return;
    root.dataset.cgVideoInit = '1';

    const video = root.querySelector('[data-cg-video-el]');
    const playBtn = root.querySelector('[data-cg-video-play]');
    if (!video || !playBtn) return;

    playBtn.addEventListener('click', function () {
      // Guard: no <source> wired up — do nothing.
      if (!video.querySelector('source') && !video.getAttribute('src')) return;
      root.classList.add('is-playing');
      video.setAttribute('controls', '');
      const played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(function () {});
      }
    });

    // When playback finishes, reset to the poster + play overlay so the
    // section doesn't sit frozen on the last frame and the video is
    // easy to replay. video.load() restores the poster frame.
    video.addEventListener('ended', function () {
      root.classList.remove('is-playing');
      video.removeAttribute('controls');
      video.load();
    });
  }

  /* Section G — center-focus carousel of 3 cards. Clicking a side card
     — or swiping horizontally — rotates it to the center (infinite
     loop). Position is held in the --pos-0/1/2 class (0 left, 1 center,
     2 right). The carousel runs at every breakpoint; CSS handles the
     mobile vs desktop proportions. */
  function initWellness(stage) {
    if (stage.dataset.cgWellnessInit === '1') return;
    stage.dataset.cgWellnessInit = '1';

    const cards = Array.prototype.slice.call(
      stage.querySelectorAll('[data-cg-wellness-card]')
    );
    if (cards.length !== 3) return;

    const POS = ['cg-spotlight__wellness-card--pos-0',
      'cg-spotlight__wellness-card--pos-1',
      'cg-spotlight__wellness-card--pos-2'];

    function posOf(card) {
      if (card.classList.contains(POS[0])) return 0;
      if (card.classList.contains(POS[2])) return 2;
      return 1;
    }

    function setPos(card, p) {
      card.classList.remove(POS[0], POS[1], POS[2]);
      card.classList.add(POS[p]);
      card.setAttribute('aria-disabled', p === 1 ? 'true' : 'false');
    }

    // Apply a rotation: every card moves `shift` positions (mod 3).
    function rotate(shift) {
      cards.forEach(function (c) {
        setPos(c, (posOf(c) + shift) % 3);
      });
    }

    // Gesture state. `swiped` is set by a swipe so the click it may
    // also fire afterwards is ignored.
    let startX = null;
    let startY = null;
    let swiped = false;

    cards.forEach(function (card) {
      setPos(card, posOf(card));
      card.addEventListener('click', function () {
        if (swiped) return; // the gesture was a swipe, not a tap
        const p = posOf(card);
        if (p === 1) return; // already centered
        // left card tapped -> shift +1; right card -> shift +2 (mod 3)
        rotate(p === 0 ? 1 : 2);
      });
      // Keyboard: arrow keys rotate the carousel from any focused card,
      // so it is fully operable without a pointer (WCAG 2.1.1).
      card.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          rotate(2);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          rotate(1);
        }
      });
    });

    // Touch/pointer swipe — a horizontal drag rotates the carousel.
    // Swiping left brings the right-hand card to the center, swiping
    // right brings the left one. The distance threshold and the
    // dx>dy check let vertical page scrolling pass through untouched.
    // Listeners live on the stage element (not window) so they are
    // garbage-collected with it on a Theme Editor re-render. Pointer
    // capture is deliberately NOT used: capturing retargets the
    // synthesized click off the card onto the stage, which would break
    // tap/click rotation.
    stage.addEventListener('pointerdown', function (e) {
      startX = e.clientX;
      startY = e.clientY;
      swiped = false;
    });

    stage.addEventListener('pointerup', function (e) {
      if (startX === null) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      startX = null;
      startY = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      swiped = true;
      rotate(dx < 0 ? 2 : 1);
    });

    stage.addEventListener('pointercancel', function () {
      startX = null;
      startY = null;
    });
  }

  function init(scope) {
    const ctx = scope && scope.querySelectorAll ? scope : document;
    ctx.querySelectorAll('[data-cg-carousel]').forEach(initCarousel);
    ctx.querySelectorAll('[data-cg-video]').forEach(initVideo);
    ctx.querySelectorAll('[data-cg-wellness]').forEach(initWellness);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-init when a section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
